// Who may be handed to a courier, and who may move on to the courier desk.
// Pure — shared by the desktop desk, the bulk bar and the phone screens, so
// the three can never disagree about what is safe to ship.
import { dispatchReadiness } from './dispatchReadiness';
import { isPartialCod, canShipCod, codAdvance } from './codAmount';
import { extractAwb } from '../ui/shared/courierUtils';

export const COURIER_LABELS = { shiprocket: 'Shiprocket', dtdc: 'DTDC', india_post: 'India Post', ekart: 'Ekart' };
export const courierLabel = (c) => COURIER_LABELS[c] || c || '';

/**
 * Split a selection into orders that can take `courier` now and those that
 * cannot, with the reason. The SAME gates as single-order assignment:
 *  - an order with an AWB is committed to its courier (no double-shipping);
 *  - dispatch readiness (COD call-confirmed, prepaid VERIFIED, partial-COD
 *    advance in, total still matching the money taken);
 *  - a Do-Not-Ship flag skips the order (single assign asks; bulk never ships it).
 * 'hold' is not a courier, so only the AWB lock applies to it.
 *
 * @param {object[]} orders
 * @param {string} courier  india_post | dtdc | shiprocket | hold
 * @param {(o:object)=>({reason?:string}|null|undefined)} [dnsFor]
 */
export function planCourierAssignment(orders = [], courier, dnsFor) {
    const ready = [];
    const blocked = [];
    const realCourier = !!courier && courier !== 'hold';
    for (const o of orders) {
        const awb = extractAwb(o);
        if (awb && realCourier) { blocked.push({ order: o, reason: `Already booked (AWB ${awb})` }); continue; }
        if (realCourier) {
            const r = dispatchReadiness(o);
            if (!r.ok) { blocked.push({ order: o, reason: r.label || 'Not ready to ship' }); continue; }
            const flag = typeof dnsFor === 'function' ? dnsFor(o) : null;
            if (flag) { blocked.push({ order: o, reason: `Do Not Ship${flag.reason ? `: ${flag.reason}` : ''}` }); continue; }
        }
        ready.push(o);
    }
    return { ready, blocked };
}

/** Reasons an order cannot move to its courier desk yet (empty = can move). */
export function moveBlockReasons(order = {}) {
    const reasons = [];
    if (!order.customer?.name) reasons.push('Customer name missing');
    if (!order.customer?.mobile || String(order.customer.mobile).replace(/\D/g, '').length < 10) reasons.push('Phone invalid');
    if (!order.customer?.pincode || String(order.customer.pincode).replace(/\D/g, '').length !== 6) reasons.push('Pincode invalid');
    if (!order.customer?.address) reasons.push('Address missing');
    if (!order.items || (Array.isArray(order.items) ? order.items.length === 0 : Object.keys(order.items).length === 0)) reasons.push('Items missing');
    if (!order.shipmentSorting?.courier || order.shipmentSorting.courier === 'hold') reasons.push('Courier not assigned');
    if (order.payment?.mode === 'COD' && order.codConfirmation?.status !== 'confirmed') reasons.push('COD not confirmed');
    if (isPartialCod(order) && !canShipCod(order)) reasons.push(`₹${codAdvance(order)} advance not collected`);
    // The rest of the payment gate (prepaid not verified, amount drift, discount waiting
    // for a manager) — COD cases are already worded above.
    const r = dispatchReadiness(order);
    if (!r.ok && !['cod', 'cod_advance'].includes(r.kind)) reasons.push(r.label || 'Not ready to ship');
    return reasons;
}
